import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { ActivityCard } from '../../components/Cards';
import { Chip, Empty, Loading } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';

const TABS = [
  { key: 'bookings', label: '📅 Bookings' },
  { key: 'orders', label: '🧾 Orders' },
  { key: 'rides', label: '🚕 Rides' },
  { key: 'applications', label: '💼 Applications' },
];

const EMPTY_TEXT = {
  bookings: 'Book a home service, salon appointment or anything else and it shows up here.',
  orders: 'Order groceries, medicines or food from local shops to see them here.',
  rides: 'Book a cab, taxi or e-rickshaw and track it from here.',
  applications: 'Apply to jobs on KAAMWALA and track every application here.',
};

/** "My activity": bookings, orders, rides and job applications in one place. */
const ActivityPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'bookings';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fetchers = {
        bookings: api.bookings.mine,
        orders: api.orders.mine,
        rides: api.rides.mine,
        applications: api.jobs.myApplications,
      };
      const res = await fetchers[tab]({ limit: 30 });
      setItems(res.data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const mapItem = (item) => ({
    bookings: {
      title: item.serviceTitle,
      subtitle: `${(item.partner && item.partner.businessName) || ''} · ${item.bookingNo}`,
      amount: item.totalAmount,
      icon: (item.category && item.category.icon) || '📅',
      date: item.scheduledDate,
      onClick: () => navigate(`/bookings/${item._id}`),
    },
    orders: {
      title: `${item.items && item.items.length} item${item.items && item.items.length > 1 ? 's' : ''} from ${(item.partner && item.partner.businessName) || 'shop'}`,
      subtitle: item.orderNo,
      amount: item.totalAmount,
      icon: '🧾',
      date: item.createdAt,
      onClick: () => navigate(`/orders/${item._id}`),
    },
    rides: {
      title: `${(item.rideType || item.vehicleType || '').toUpperCase()} · ${(item.estimate && item.estimate.distanceKm) || item.distanceKm || 0} km`,
      subtitle: (item.drop && item.drop.address) || item.rideNo,
      amount: (item.final && item.final.total) || (item.estimate && item.estimate.total) || item.finalFare || item.estimatedFare,
      icon: item.serviceType === 'courier' ? '📦' : '🚕',
      date: item.createdAt,
      onClick: () => navigate(`/ride/${item._id}`),
    },
    applications: {
      title: (item.job && item.job.title) || 'Job application',
      subtitle: (item.job && item.job.companyName) || '',
      amount: item.expectedSalary || 0,
      icon: '💼',
      date: item.createdAt,
      onClick: () => navigate(`/jobs/${item.job && item.job._id}`),
    },
  }[tab]);

  return (
    <div>
      <PageHeader
        title="My Activity"
        subtitle="Bookings, orders, rides and applications"
        back={false}
      />

      <div className="row no-bar" style={{ gap: 8, overflowX: 'auto', marginBottom: 16 }}>
        {TABS.map((t) => (
          <Chip
            key={t.key}
            label={t.label}
            active={tab === t.key}
            onClick={() => setParams({ tab: t.key })}
          />
        ))}
      </div>

      {loading ? <Loading /> : !items.length ? (
        <Empty
          emoji="🗂️"
          title={`No ${tab} yet`}
          text={EMPTY_TEXT[tab]}
          actionLabel="Explore KAAMWALA"
          onAction={() => navigate('/home')}
        />
      ) : (
        <div className="grid grid-list">
          {items.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 45)}>
              <ActivityCard {...mapItem(item)} status={item.status} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityPage;
